import { ErrorBoundary, type ErrorBoundaryProps } from '@sentry/react'
import dayjs from 'dayjs'
import { type ReactNode, useEffect, useState } from 'react'
import { ReloadBanner } from './components'
import FinalWarningModal from './components/FinalWarningModal'
import UpdatePromptModal from './components/updateModal'

const INTERVALS_MINUTES = [20, 60, 120]
const FINAL_WARNING_MINUTES = 10
const MINUTE_IN_MS = 60000

const LazyImportErrorBoundary = ({ children }: { children: ReactNode }) => {
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showFinalWarningModal, setShowFinalWarningModal] = useState(false)
  const [showBanner, setShowBanner] = useState(false)
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0)
  const [modalInteracted, setModalInteracted] = useState(true)
  const [forceRefresh, setforceRefresh] = useState(false)

  const onReset: ErrorBoundaryProps['onError'] = ({ message }) => {
    if (message.includes('Failed to fetch dynamically imported module')) {
      setShowBanner(true)
      setModalInteracted(false)
    }
  }

  const fetchVersion = () => fetch('/version.txt').then((res) => res.text())

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchVersion()
      .then((res) => localStorage.setItem('version', res))
      .catch((error) => {
        console.log(error || 'Error fetching version')
      })
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const interval = setInterval(() => {
      fetchVersion()
        .then((res) => {
          if (localStorage.getItem('version') !== res) {
            setShowBanner(true)
          }
        })
        .catch((error) => {
          console.log(error || 'Error fetching version')
        })
    }, MINUTE_IN_MS)

    return () => clearInterval(interval)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (showBanner && !showUpdateModal && modalInteracted) {
      // Only start the timer if the modal has been interacted with (closed)
      const nextReminderTime =
        INTERVALS_MINUTES[currentIntervalIndex] * MINUTE_IN_MS
      const timer = setTimeout(() => {
        setShowUpdateModal(true)
        setModalInteracted(false)
      }, nextReminderTime)

      return () => clearTimeout(timer)
    }
  }, [showBanner, currentIntervalIndex, modalInteracted])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // Proceed with final warning and forced refresh if all reminders have been shown
    const timeoutUntilFinalWarning =
      calculateTimeUntilFinalWarning() - 10 * MINUTE_IN_MS
    if (forceRefresh) {
      if (timeoutUntilFinalWarning > 0) {
        setTimeout(() => {
          // Show the final warning and count down from 10 minutes
          setShowFinalWarningModal(true)
          setTimeout(() => {
            handleRefreshNow()
          }, FINAL_WARNING_MINUTES * MINUTE_IN_MS)
        }, timeoutUntilFinalWarning)
      }
    }
  }, [forceRefresh])

  const handleRefreshNow = () => {
    localStorage.removeItem('version')
    window.location.reload()
  }

  const handleRemindLater = () => {
    setShowUpdateModal(false)
    setModalInteracted(true)

    const nextIndex = currentIntervalIndex + 1
    if (nextIndex < INTERVALS_MINUTES.length) {
      setCurrentIntervalIndex(nextIndex)
    } else {
      setModalInteracted(false)
      setforceRefresh(true)
    }
  }

  // Calculate time until 10 minutes before midnight
  const calculateTimeUntilFinalWarning = () => {
    const now = dayjs()
    const midnight = dayjs().endOf('day')
    const finalWarningBeforeMidnight = midnight.subtract(
      FINAL_WARNING_MINUTES,
      'minute'
    )

    // Return the difference in milliseconds
    return finalWarningBeforeMidnight.diff(now)
  }

  return (
    <ErrorBoundary onError={onReset}>
      {showBanner && <ReloadBanner />}
      {showUpdateModal && (
        <UpdatePromptModal
          isOpen={showUpdateModal}
          onRefresh={handleRefreshNow}
          onRemindLater={handleRemindLater}
        />
      )}
      {showFinalWarningModal && (
        <FinalWarningModal
          isOpen={showFinalWarningModal}
          onAcknowledged={() => setShowFinalWarningModal(false)}
          onRefresh={handleRefreshNow}
        />
      )}
      {children}
    </ErrorBoundary>
  )
}

export default LazyImportErrorBoundary
